import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { FacebookPage, InstagramBusinessAccount, backendApi, GetPagesResponse } from '@/common/reducers/backend-api/backend-api';
import { AuthToken } from '@/common/models/auth-token';
import { OAuthTokenData } from './OAuthTestSlice';
import { appConfig } from '@/common/config/app.config';
import { openOAuthPopup, createOAuthMessageHandler } from '@/common/utils/oauth-popup';
import { selectUserId } from './UserSelectors';
import type { CommonRootState } from '@common/types/common-root-state-type';

// Temporary types if not imported - replace with actual imports

// Types for the Instagram selector state
interface InstagramSelectorState {
  facebookConnected: boolean;
  selectedInstagramAccountId: string | null;
  selectedInstagramAccount: InstagramBusinessAccount | null;
  instagramAccounts: InstagramBusinessAccount[];
  facebookPages: FacebookPage[];
  loading: boolean;
  error: string | null;
  connecting: boolean;
}

// Initial state
const initialState: InstagramSelectorState = {
  facebookConnected: false,
  selectedInstagramAccountId: null,
  selectedInstagramAccount: null,
  instagramAccounts: [],
  facebookPages: [],
  loading: false,
  error: null,
  connecting: false
};

// Thunk to check Facebook token validity
export const checkFacebookTokenValidity = createAsyncThunk(
  'instagramSelector/checkFacebookTokenValidity',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // Use RTK Query mutation
      const response = await dispatch(
        backendApi.endpoints.validateFacebookToken.initiate()
      );
      
      // Type guard to check if response contains an error
      if ('error' in response) {
        const errorMessage = 
          typeof response.error === 'object' && response.error !== null
            ? 'status' in response.error && 'data' in response.error
              // Handle FetchBaseQueryError
              ? (response.error.data as any)?.message || 'Failed to validate Facebook token'
              // Handle SerializedError
              : (response.error as any).message || 'Failed to validate Facebook token'
            : 'Failed to validate Facebook token';
        
        return rejectWithValue(errorMessage);
      }
      
      // We now know response contains data
      const { data } = response;
      
      // Return the validation result
      return data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// Thunk to authenticate with Facebook
export const connectFacebook = createAsyncThunk<unknown, void, { state: CommonRootState }>(
  'instagramSelector/connectFacebook',
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      // Set connecting state
      dispatch(setConnecting(true));
      const state = getState();
      
      // Define OAuth parameters
      const scopes = 'pages_show_list,instagram_basic,pages_read_engagement,pages_messaging,pages_manage_metadata,instagram_manage_messages';
      const botId = 'trigger-selector';
      const userId = selectUserId(state);
      
      const authUrl = `${appConfig.API_ENDPOINT}/oauth/facebook`;
      const queryParams = new URLSearchParams({
        userId: userId ?? '',
        botId,
        scope: scopes
      });
      
      const fullUrl = `${authUrl}?${queryParams.toString()}`;
      
      // Create a promise to handle the authentication flow
      const authResult = await new Promise<{success: boolean, tokens?: OAuthTokenData, error?: string}>((resolve) => {
        let isResolved = false;
        
        const resolveOnce = (result: {success: boolean, tokens?: OAuthTokenData, error?: string}) => {
          if (!isResolved) {
            isResolved = true;
            resolve(result);
          }
        };
        
        // Create message handler for OAuth window
        const messageHandler = createOAuthMessageHandler(
          'facebook_auth_success',
          'facebook_auth_error',
          (data) => {
            // Handle successful authentication
            const tokens: OAuthTokenData = {
              accessToken: data.accessToken,
              refreshToken: data.refreshToken,
              tokenExpiry: data.tokenExpiry || (data.expiresIn ? Date.now() + (data.expiresIn * 1000) : undefined),
              email: data.email,
              provider: 'facebook'
            };
            
            resolveOnce({ success: true, tokens });
          },
          (error) => {
            // Handle authentication error
            resolveOnce({ success: false, error });
          }
        );
        
        // Open the OAuth popup
        const popup = openOAuthPopup({
          url: fullUrl,
          title: 'Connect Facebook',
          onMessage: messageHandler,
          onClose: () => {
            // Only resolve if not already resolved
            resolveOnce({ success: false, error: 'Authentication canceled' });
          }
        });
      });
      
      // If authentication was successful, dispatch action to fetch pages
      if (authResult.success && authResult.tokens) {
        // Return the tokens
        return authResult.tokens;
      } else {
        return rejectWithValue(authResult.error || 'Authentication failed');
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      dispatch(setConnecting(false));
    }
  }
);

// Thunk to fetch Instagram pages
export const fetchInstagramPages = createAsyncThunk<
  GetPagesResponse,
  void,
  { rejectValue: string }
>(
  'instagramSelector/fetchPages',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      
      // First validate the Facebook token
      const tokenValidationResult = await dispatch(checkFacebookTokenValidity());
      
      if (checkFacebookTokenValidity.fulfilled.match(tokenValidationResult)) {
        if (!tokenValidationResult.payload.isValid) {
          return rejectWithValue('Facebook not connected or token invalid');
        }
      } else {
        return rejectWithValue('Failed to validate Facebook token');
      }
      
      const response = await dispatch(
        backendApi.endpoints.getFacebookPages.initiate()
      );
      
      if ('error' in response) {
        const errorMessage = 
          typeof response.error === 'object' && response.error !== null
            ? 'status' in response.error && 'data' in response.error
              ? (response.error.data as any)?.message || 'Failed to fetch pages'
              : (response.error as any).message || 'Failed to fetch pages'
            : 'Failed to fetch pages';
        return rejectWithValue(errorMessage);
      }
      
      const { data } = response;
      
      // Check if data structure is valid (both arrays should exist)
      if (!data || !Array.isArray(data.facebookPages) || !Array.isArray(data.instagramAccounts)) {
         return rejectWithValue('Invalid response structure from server');
      }
      
      // Return the full data object
      return data as GetPagesResponse; 
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Thunk to disconnect Facebook
export const disconnectFacebook = createAsyncThunk(
  'instagramSelector/disconnectFacebook',
  async (facebookAuthTokenId: string | undefined, { dispatch, rejectWithValue }) => {
    try {
      if (facebookAuthTokenId) {
        // Use RTK Query deletion mutation instead of direct fetch
        const response = await dispatch(
          backendApi.endpoints.deleteAuthToken.initiate(facebookAuthTokenId)
        );
        
        // Type guard to check if response contains an error
        if ('error' in response) {
          const errorMessage = 
            typeof response.error === 'object' && response.error !== null
              ? 'status' in response.error && 'data' in response.error
                // Handle FetchBaseQueryError
                ? (response.error.data as any)?.message || 'Failed to delete auth token'
                // Handle SerializedError
                : (response.error as any).message || 'Failed to delete auth token'
              : 'Failed to delete auth token';
          
          return rejectWithValue(errorMessage);
        }
      }
      
      return { success: true };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// Create the slice
export const instagramSelectorSlice = createSlice({
  name: 'instagramSelector',
  initialState,
  reducers: {
    setConnecting: (state, action: PayloadAction<boolean>) => {
      state.connecting = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setSelectedInstagramAccountId: (state, action: PayloadAction<string>) => {
      state.selectedInstagramAccountId = action.payload;
      state.selectedInstagramAccount = state.instagramAccounts.find(p => p.id === action.payload) || null;
    },
    setFacebookConnected: (state, action: PayloadAction<boolean>) => {
      state.facebookConnected = action.payload;
    },
    resetState: (state) => {
      state.facebookConnected = false;
      state.selectedInstagramAccountId = null;
      state.selectedInstagramAccount = null;
      state.instagramAccounts = [];
      state.facebookPages = [];
      state.error = null;
      state.connecting = false;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // checkFacebookTokenValidity
      .addCase(checkFacebookTokenValidity.fulfilled, (state, action) => {
        state.facebookConnected = action.payload.isValid;
        state.error = null;
      })
      .addCase(checkFacebookTokenValidity.rejected, (state, action) => {
        state.facebookConnected = false;
        state.error = action.payload as string || 'Failed to validate Facebook token';
      })
      
      // connectFacebook
      .addCase(connectFacebook.fulfilled, (state) => {
        state.facebookConnected = true;
        state.error = null;
      })
      .addCase(connectFacebook.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to connect to Facebook';
      })
      
      // fetchInstagramPages
      .addCase(fetchInstagramPages.fulfilled, (state, action) => {
        state.facebookPages = action.payload.facebookPages;
        state.instagramAccounts = action.payload.instagramAccounts;
        state.error = null;
        
        if (state.instagramAccounts.length > 0 && !state.selectedInstagramAccountId) {
          state.selectedInstagramAccountId = state.instagramAccounts[0].id;
          state.selectedInstagramAccount = state.instagramAccounts[0];
        } else if (state.selectedInstagramAccountId) {
          state.selectedInstagramAccount = state.instagramAccounts.find(p => p.id === state.selectedInstagramAccountId) || null;
        } else {
            state.selectedInstagramAccountId = null;
            state.selectedInstagramAccount = null;
        }
      })
      .addCase(fetchInstagramPages.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to fetch pages';
        state.facebookPages = [];
        state.instagramAccounts = [];
        state.selectedInstagramAccountId = null;
        state.selectedInstagramAccount = null;
      })
      
      // disconnectFacebook
      .addCase(disconnectFacebook.fulfilled, (state) => {
        state.facebookConnected = false;
        state.selectedInstagramAccountId = null;
        state.selectedInstagramAccount = null;
        state.instagramAccounts = [];
        state.facebookPages = [];
        state.error = null;
      })
      .addCase(disconnectFacebook.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to disconnect Facebook';
      });
  }
});

// Export actions
export const { setConnecting, setLoading, setSelectedInstagramAccountId, setFacebookConnected, resetState } = instagramSelectorSlice.actions;

// Export selectors
export const selectInstagramSelectorState = (state: any) => state.InstagramSelectorReducer;
export const selectIsFacebookConnected = (state: any) => state.InstagramSelectorReducer.facebookConnected;
export const selectSelectedInstagramAccount = (state: any) => state.InstagramSelectorReducer.selectedInstagramAccount;
export const selectInstagramAccounts = (state: any) => state.InstagramSelectorReducer.instagramAccounts;
export const selectFacebookPages = (state: any) => state.InstagramSelectorReducer.facebookPages;
export const selectInstagramError = (state: any) => state.InstagramSelectorReducer.error;
export const selectIsLoading = (state: any) => state.InstagramSelectorReducer.loading;
export const selectIsConnecting = (state: any) => state.InstagramSelectorReducer.connecting;

// Export reducer
export default instagramSelectorSlice.reducer; 